import React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ErrorIcon from '@material-ui/icons/Error';
import {
  DEFAULT_MESSAGE_STATUS,
  MESSAGE_FAILURE_STATUSES
} from 'features/message-center/constants';
import { useElementWidth } from 'hooks';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { extractFilenameFromUrl, formatInternationalPhone } from 'UI/utils';

import {
  AlternateMessage,
  Badge,
  ContactName,
  ContentContainer,
  Header,
  IconContainer,
  ListItem,
  MessageContainer,
  MessageText,
  StyledListItemIcon
} from './ConversationListItem.styled';
import { formatDate } from './ConversationListItem.utils';

const MIN_WIDTH_THRESHOLD = 280;

export const ConversationListItem = ({
  conversation,
  isChecked,
  isSelected,
  minWidthThreshold = MIN_WIDTH_THRESHOLD,
  onClickCheckbox,
  onClickConversation,
  shouldTruncate = false,
  showCheckbox = false
}) => {
  const { ref: listItemRef, width } = useElementWidth();
  const isWideEnough = width >= minWidthThreshold;

  const {
    attachments,
    date,
    draftAttachment,
    draftMessage,
    excerpt,
    externalNumber,
    fullName = '',
    message,
    messageStatus = DEFAULT_MESSAGE_STATUS,
    unreadMessages = 0
  } = conversation;

  const hasErrorStatus = MESSAGE_FAILURE_STATUSES.includes(messageStatus);
  const hasAttachments = attachments?.length > 0;
  const hasUnreadMessages = unreadMessages > 0;
  const phoneNumber = formatInternationalPhone(externalNumber);

  const alternativeMessage = hasAttachments
    ? extractFilenameFromUrl(attachments[0]?.url ?? attachments[0])
    : '';

  const handleClickConversation = () => {
    onClickConversation && onClickConversation(conversation);
  };

  const handleClickCheckbox = () => {
    onClickCheckbox && onClickCheckbox({ id: conversation.id, isSelected: !isChecked });
  };

  const renderMessageContent = () => {
    if (draftMessage || draftAttachment) {
      return (
        <MessageText variant="body2" shouldTruncate>
          <span className="draft-label">{strings.drafts.draft}:</span>{' '}
          <i>
            {draftMessage ||
              draftAttachment?.file_name ||
              strings.messageCenter.attachments.attachment}
          </i>
        </MessageText>
      );
    }

    return (
      <>
        {hasAttachments && (
          <IconContainer>
            <AttachFileIcon
              aria-label="message with attachment"
              color="disabled"
              fontSize="inherit"
            />
          </IconContainer>
        )}
        {message ? (
          <MessageText shouldTruncate={shouldTruncate}>{excerpt ?? message}</MessageText>
        ) : (
          <AlternateMessage variant="body2">{alternativeMessage}</AlternateMessage>
        )}
      </>
    );
  };

  return (
    <ListItem
      ref={listItemRef}
      aria-label="conversation"
      button
      onClick={showCheckbox ? handleClickCheckbox : handleClickConversation}
      selected={isSelected}
    >
      {showCheckbox && (
        <StyledListItemIcon>
          <Checkbox checked={isChecked} color="primary" disableRipple edge="end" />
        </StyledListItemIcon>
      )}
      <ListItemText
        primary={
          <>
            <Header>
              <Typography variant="body2">
                <When condition={fullName}>
                  <ContactName>{fullName}</ContactName>
                </When>
                <When condition={!fullName || isWideEnough}>
                  <span>{phoneNumber}</span>
                </When>
              </Typography>
              <Typography variant="body2">{formatDate(date)}</Typography>
            </Header>

            <When condition={fullName && !isWideEnough}>
              <Typography variant="body2" gutterBottom>
                {phoneNumber}
              </Typography>
            </When>

            <ContentContainer>
              <MessageContainer>{renderMessageContent()}</MessageContainer>

              {hasErrorStatus ? (
                <IconContainer>
                  <ErrorIcon fontSize="inherit" aria-label="message with error" color="error" />
                </IconContainer>
              ) : (
                <Badge
                  badgeContent={unreadMessages}
                  color="primary"
                  invisible={!hasUnreadMessages}
                  max={9}
                />
              )}
            </ContentContainer>
          </>
        }
      />
    </ListItem>
  );
};
